import { Tag } from 'antd';
import BaseCellText from 'common/components/table/base-cell-text';
import { ICellRendererProps } from 'common/components/table/table.types';
import { formatAddressLabel } from 'common/helpers/address.helper';
import { ICardAccessPanel } from 'common/services/card-access-service/card-access.service.types';
import {
  ICameraConfiguration,
  IFacilityCodeResponse,
  IGateConfiguration,
  IReaderConfiguration,
  IRelayConfiguration,
} from 'common/services/configuration-service/configuration.service.types';
import { IAddress } from 'common/services/street-service/street.service.types';
import { ITrafficReportRead } from 'common/services/traffic-report-service/traffic-report.service.types';
import { DaysOfWeek, daysOfWeekMap } from 'features/eiq-manage/enums/days-of-week.enum';
import { TrafficLogReportType, trafficLogReportTypeMap } from 'features/eiq-manage/enums/traffic-log-report-type.enum';
import { title } from 'process';

export const trafficReportTableInitialColumns = [
  {
    key: 'name',
    title: 'Name',
    dataKey: 'name',
    width: 200,
    minWidth: 150,
    resizable: true,
    sortable: true,
    flexGrow: 1,
  },
  {
    key: 'reportType',
    title: 'Type',
    dataKey: 'reportType',
    width: 200,
    minWidth: 150,
    resizable: true,
    sortable: true,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<ITrafficReportRead>) => {
      return trafficLogReportTypeMap[cell.rowData?.reportType as TrafficLogReportType];
    },
  },
  {
    key: 'runAt',
    title: 'Time to Run',
    dataKey: 'runAt',
    width: 200,
    minWidth: 150,
    resizable: true,
    sortable: true,
    flexGrow: 1,
  },

  {
    key: 'dayOfWeek',
    title: 'Day of Week',
    dataKey: 'dayOfWeek',
    width: 200,
    minWidth: 150,
    resizable: true,
    sortable: true,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<ITrafficReportRead>) => {
      return daysOfWeekMap[cell.cellData as DaysOfWeek];
    },
  },
  {
    key: 'dayOfMonth',
    title: 'Day of Month',
    dataKey: 'dayOfMonth',
    width: 200,
    minWidth: 150,
    resizable: true,
    sortable: true,
    flexGrow: 1,
  },
  {
    key: 'emails',
    title: 'Emails',
    dataKey: 'emails',
    width: 400,
    minWidth: 150,
    resizable: true,
    sortable: true,
    flexGrow: 1,
  },
  {
    key: 'includedAddresses',
    title: 'Included Addresses',
    dataKey: 'includedAddresses',
    width: 400,
    minWidth: 150,
    resizable: true,
    sortable: true,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<ITrafficReportRead>) => {
      var result = cell.cellData?.map((address: IAddress) => `${formatAddressLabel(address)}`);
      return result?.join('; ');
    },
  },
  {
    key: 'excludedAddresses',
    title: 'Excluded Addresses',
    dataKey: 'excludedAddresses',
    width: 400,
    minWidth: 150,
    resizable: true,
    sortable: true,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<ITrafficReportRead>) => {
      var result = cell.cellData?.map((address: IAddress) => `${formatAddressLabel(address)}`);
      return result?.join('; ');
    },
  },
];

export const gatesTableInitialColumns = [
  {
    key: 'name',
    title: 'Name',
    dataKey: 'name',
    width: 200,
    minWidth: 150,
    resizable: true,
    sortable: true,
    flexGrow: 1,
  },
  {
    key: 'longitude',
    title: 'Longitude',
    dataKey: 'longitude',
    width: 150,
    minWidth: 150,
    resizable: true,
    sortable: false,
    flexGrow: 1,
  },
  {
    key: 'latitude',
    title: 'Latitude',
    dataKey: 'latitude',
    width: 150,
    minWidth: 150,
    resizable: true,
    sortable: false,
    flexGrow: 1,
  },
  {
    key: 'useRtsp',
    title: 'Is DriveID RTSP enabled',
    dataKey: 'useRtsp',
    width: 200,
    minWidth: 200,
    resizable: true,
    sortable: false,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<IGateConfiguration>) => {
      return cell.cellData ? 'Yes' : 'No';
    },
  },
  {
    key: 'rtspUrl',
    title: 'DriveID RTSP URL',
    dataKey: 'rtspUrl',
    width: 350,
    minWidth: 350,
    resizable: true,
    sortable: false,
    flexGrow: 1,
  },
  {
    key: 'useGateController',
    title: 'Is gate controller enabled',
    dataKey: 'useGateController',
    width: 250,
    minWidth: 200,
    resizable: true,
    sortable: false,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<IGateConfiguration>) => {
      return cell.cellData ? 'Yes' : 'No';
    },
  },
  {
    key: 'cameras',
    title: 'Cameras',
    dataKey: 'cameras',
    width: 800,
    minWidth: 400,
    resizable: true,
    sortable: false,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<IGateConfiguration>) => {
      var result = cell.cellData?.map((i: ICameraConfiguration) => `${i.name} (${i.direction})`);
      return result?.join(', ');
    },
  },
  {
    key: 'readers',
    title: 'Readers',
    dataKey: 'readers',
    width: 800,
    minWidth: 400,
    resizable: true,
    sortable: false,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<IGateConfiguration>) => {
      var result = cell.cellData?.map((i: IReaderConfiguration) => `${i.number} (${i.direction})`);
      return result?.join(', ');
    },
  },
  {
    key: 'relays',
    title: 'Relays',
    dataKey: 'relays',
    width: 800,
    minWidth: 400,
    resizable: true,
    sortable: false,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<IGateConfiguration>) => {
      var result = cell.cellData?.map((i: IRelayConfiguration) => `${i.number} - ${i.name}`);
      return result?.join(', ');
    },
  },
];

export const cardClassesTableInitialColumns = [
  {
    key: 'name',
    title: 'Name',
    dataKey: 'name',
    width: 200,
    minWidth: 150,
    resizable: true,
    sortable: true,
    flexGrow: 1,
  },
  {
    key: 'value',
    title: 'Access Group ID',
    dataKey: 'value',
    width: 200,
    minWidth: 150,
    resizable: true,
    sortable: true,
    flexGrow: 1,
  },
];

export const panelsTableInitialColumns = [
  {
    key: 'panelNumber',
    title: 'Panel Number',
    dataKey: 'panelNumber',
    width: 150,
    minWidth: 150,
    resizable: true,
    sortable: true,
    flexGrow: 1,
  },
  {
    key: 'description',
    title: 'Description',
    dataKey: 'description',
    width: 550,
    minWidth: 150,
    resizable: true,
    sortable: true,
    flexGrow: 1,
  },
  {
    key: 'deviceName',
    title: 'Device Name',
    dataKey: 'deviceName',
    width: 150,
    minWidth: 150,
    resizable: true,
    sortable: true,
    flexGrow: 1,
  },
  {
    key: 'groupName',
    title: 'Group Name',
    dataKey: 'groupName',
    width: 150,
    minWidth: 150,
    resizable: true,
    sortable: true,
    flexGrow: 1,
  },
  {
    key: 'panelName',
    title: 'Panel Name',
    dataKey: 'panelName',
    width: 150,
    minWidth: 150,
    resizable: true,
    sortable: true,
    flexGrow: 1,
  },
];

export const facilityCodesTableInitialColumns = [
  {
    key: 'value',
    title: 'EIQ Facility Code',
    dataKey: 'value',
    width: 200,
    minWidth: 150,
    resizable: true,
    sortable: true,
    flexGrow: 1,
  },
  {
    key: 'cardAccessSystemValue',
    title: 'Slot',
    dataKey: 'cardAccessSystemValue',
    width: 200,
    minWidth: 150,
    resizable: true,
    sortable: false,
    flexGrow: 1,
  },
  {
    key: 'isSyncToCardAccessSystem',
    title: 'Sync to Card Access Server',
    dataKey: 'isSyncToCardAccessSystem',
    width: 250,
    minWidth: 150,
    resizable: true,
    sortable: false,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<IFacilityCodeResponse>) => {
      return cell.cellData ? 'Yes' : 'No';
    },
  },
  {
    key: 'defaultForType',
    title: 'Used by default for',
    dataKey: 'defaultForType',
    width: 200,
    minWidth: 150,
    resizable: true,
    sortable: false,
    flexGrow: 1,
  },
];
